import { css } from "@emotion/react";
import {
  Colors,
  Grid,
  SkeletonBlock,
  SkeletonLoader,
  Typography,
} from "@zapier/design-system";
import { Icon } from "@zapier/design-system";

import { useTrialActions } from "@zapier/trials/src/hooks";
import { isAccountEligibleForAction } from "@zapier/trials/src/utils";
import { emitEvent } from "@zapier/trials/src/utils";
import { InteractionName } from "@zapier/trials/src/utils/emitEvent/trackingTypes";

import type { Account, Action } from "@zapier/trials/src/types";

const Styles = {
  root: css({
    margin: 0,
    padding: 0,
    listStyle: "none",
    width: "100%",
  }),

  listItem: css({
    margin: 0,
    padding: 0,
    width: "100%",

    "&:not(:last-child)": {
      marginBottom: "10px",
    },
  }),

  listItemLink: css({
    alignItems: "center",
    boxSizing: "border-box",
    borderRadius: "4px",
    color: Colors.neutral700,
    display: "flex",
    height: "42px",
    padding: "0 24px",
    textDecoration: "none",
    width: "100%",

    ...Typography.paragraph3Semibold,

    "> *": {
      display: "flex",
    },

    "&:hover, &:focus": {
      backgroundColor: Colors.acidWash,
      outline: "none",

      "> :last-child": {
        opacity: 1,
      },
    },
  }),

  listItemIcon: css({
    marginRight: "10px",
    minWidth: "24px",
  }),

  listItemTitle: css({
    flex: 1,
  }),

  listItemCTA: css({
    alignItems: "center",
    color: Colors.blue600,
    gap: "5px",
    opacity: 0,
    textDecoration: "underline",
    ...Typography.paragraph3,
  }),
};

type Props = {
  account: Account;
  builderApiKey?: string;
};

const fallbackAction: Action = {
  icon: "miscBoltAlt",
  action: {
    href: "/webintent/create-zap?entry-point-method=make_a_zap_call_to_action",
    buttonLabel: "Show me where",
    isExternalHref: false,
  },
  id: "create-zap",
  conditions: {
    isTrialingPro: true,
  },
  title: "Build your first Zap in minutes",
};

export const ActionsList = ({ builderApiKey = "", account }: Props) => {
  const { actions, error, isLoading } = useTrialActions({ builderApiKey });

  if (error) {
    actions.push(fallbackAction);
  }

  const eligibleActions = actions.filter((action) => {
    return account && isAccountEligibleForAction({ action, account });
  });

  const onActionClick = (action: Action) => {
    emitEvent(InteractionName.trialHubActionClicked, {
      event_action: "click",
      option_selected: action.id,
      destination_url_address: action.action.href,
    });
  };

  return !isLoading ? (
    <ul css={Styles.root}>
      {eligibleActions.map((action) => {
        return (
          <li key={action.id} css={Styles.listItem}>
            {/* eslint-disable-next-line react/jsx-no-target-blank */}
            <a
              href={action.action.href || "#"}
              css={Styles.listItemLink}
              target={action.action.isExternalHref ? "_blank" : undefined}
              rel="noopener"
              onClick={() => onActionClick(action)}
            >
              <span css={Styles.listItemIcon}>
                <Icon name={action.icon} size={24} />
              </span>
              <span css={Styles.listItemTitle}>{action.title}</span>
              <span css={Styles.listItemCTA}>
                <span>{action.action.buttonLabel || "Show me where"}</span>
                {action.action.isExternalHref && (
                  <Icon
                    ariaLabel="External Link"
                    name="arrowOffsite"
                    size={12}
                  />
                )}
              </span>
            </a>
          </li>
        );
      })}
    </ul>
  ) : (
    <SkeletonLoader>
      <Grid gap={10}>
        {Array.from({ length: 5 }).map((each, index) => {
          return <SkeletonBlock key={index} height={42} />;
        })}
      </Grid>
    </SkeletonLoader>
  );
};
